import { useMemo, useState } from "react";

function getAmount(transfer) {
  const amount = Number(transfer.amount);
  return Number.isFinite(amount) && amount > 0 ? amount : null;
}

function formatAmount(amount) {
  if (!Number.isFinite(amount) || amount <= 0) return "Not available";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(amount);
}

function formatDate(date) {
  if (!date) return "Unknown date";
  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" }).format(new Date(date));
}

function smoothPath(points) {
  if (!points.length) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  return points.reduce((path, point, index) => {
    if (index === 0) return `M ${point.x} ${point.y}`;
    const previous = points[index - 1];
    const midpoint = (previous.x + point.x) / 2;
    return `${path} C ${midpoint} ${previous.y}, ${midpoint} ${point.y}, ${point.x} ${point.y}`;
  }, "");
}

export default function ReportTransferChart({ transfers = [], hideSummary = false }) {
  const [view, setView] = useState("chart");
  const records = useMemo(
    () => [...transfers].filter((transfer) => transfer.date).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
    [transfers],
  );
  const valuedRecords = records.filter((transfer) => getAmount(transfer) !== null);
  const highest = Math.max(...valuedRecords.map(getAmount), 0);
  const latest = valuedRecords.length ? getAmount(valuedRecords[valuedRecords.length - 1]) : null;
  const chartWidth = 980;
  const chartHeight = 300;
  const padding = { top: 28, right: 26, bottom: 50, left: 70 };
  const plotWidth = chartWidth - padding.left - padding.right;
  const plotHeight = chartHeight - padding.top - padding.bottom;
  const maxAmount = Math.max(highest * 1.08, 1);
  const points = valuedRecords.map((transfer, index) => ({
    ...transfer,
    x: padding.left + (valuedRecords.length === 1 ? plotWidth / 2 : (index / (valuedRecords.length - 1)) * plotWidth),
    y: padding.top + plotHeight - (getAmount(transfer) / maxAmount) * plotHeight,
  }));
  const linePath = smoothPath(points);
  const areaPath = points.length ? `${linePath} L ${points[points.length - 1].x} ${padding.top + plotHeight} L ${points[0].x} ${padding.top + plotHeight} Z` : "";
  const guideValues = [0, 0.25, 0.5, 0.75, 1].map((ratio) => ratio * maxAmount);

  return (
    <div className="pr-transfer-body">
      <div className="pr-transfer-heading">
        {!hideSummary && (
          <div className="pr-transfer-summary">
            <span className="pr-transfer-summary-item">
              <span className="pr-transfer-summary-label">Recorded moves</span>
              <strong>{records.length}</strong>
            </span>
            <span className="pr-transfer-summary-divider" />
            <span className="pr-transfer-summary-item">
              <span className="pr-transfer-summary-label">Latest value</span>
              <strong>{latest != null ? formatAmount(latest) : "—"}</strong>
            </span>
            <span className="pr-transfer-summary-divider" />
            <span className="pr-transfer-summary-item">
              <span className="pr-transfer-summary-label">Highest value</span>
              <strong>{highest > 0 ? formatAmount(highest) : "—"}</strong>
            </span>
          </div>
        )}
        <div className="pr-view-toggle pr-transfer-view-toggle" role="group" aria-label="Transfer value view">
          <button type="button" className={view === "chart" ? "is-active" : ""} onClick={() => setView("chart")} aria-label="Show transfer value chart" aria-pressed={view === "chart"} title="Chart">
            <span className="pr-view-icon pr-view-icon--line" aria-hidden="true" />
          </button>
          <button type="button" className={view === "numeric" ? "is-active" : ""} onClick={() => setView("numeric")} aria-label="Show transfer values" aria-pressed={view === "numeric"} title="Numeric values">
            <span className="pr-view-icon pr-view-icon--numbers" aria-hidden="true">#</span>
          </button>
        </div>
      </div>

      {records.length === 0 ? (
        <div className="pr-transfer-empty">No transfer history returned for this player.</div>
      ) : view === "chart" ? (
        valuedRecords.length > 0 ? (
          <div className="pr-transfer-chart-wrap">
            <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="pr-transfer-chart" role="img" aria-label="Transfer value history">
              <defs>
                <linearGradient id="pr-transfer-area-fill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor="#9aaab8" stopOpacity="0.28" />
                  <stop offset="1" stopColor="#6c7885" stopOpacity="0.06" />
                </linearGradient>
              </defs>
              {guideValues.map((value) => {
                const y = padding.top + plotHeight - (value / maxAmount) * plotHeight;
                return (
                  <g key={value}>
                    <line x1={padding.left} y1={y} x2={chartWidth - padding.right} y2={y} className="pr-transfer-grid-line" />
                    <text x={padding.left - 12} y={y + 5} textAnchor="end" className="pr-transfer-axis-label">{formatAmount(value)}</text>
                  </g>
                );
              })}
              {records.map((transfer) => {
                const point = points.find((item) => item.id === transfer.id);
                return point ? <line key={`marker-${transfer.id}`} x1={point.x} y1={padding.top} x2={point.x} y2={padding.top + plotHeight} className="pr-transfer-marker-line" /> : null;
              })}
              <path d={areaPath} className="pr-transfer-area" />
              <path d={linePath} className="pr-transfer-line" />
              {points.map((point) => (
                <g key={point.id}>
                  <circle cx={point.x} cy={point.y} r="6" className="pr-transfer-point" />
                  <text x={point.x} y={chartHeight - 18} textAnchor="middle" className="pr-transfer-date">{new Date(point.date).getFullYear()}</text>
                </g>
              ))}
            </svg>
          </div>
        ) : (
          <div className="pr-transfer-empty">Transfer dates are available, but no monetary values were returned.</div>
        )
      ) : (
        <div className="pr-transfer-list">
          {records.map((transfer) => (
            <div className="pr-transfer-row" key={transfer.id}>
              <div>
                <strong>{transfer.toteam?.name || "Unknown destination"}</strong>
                <span>{transfer.fromteam?.name || "Unknown origin"} · {formatDate(transfer.date)}</span>
              </div>
              <b>{formatAmount(getAmount(transfer))}</b>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
